import { fetchAbaRecord } from '@/api/aba-api';
import { PublicStoreHeader } from '@/components/orders/PublicStoreHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AbaRecord, AbaRecordStatus } from '@/types/aba-record-type';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  RefreshCcw,
  Smartphone,
} from 'lucide-react';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { useParams } from 'wouter';

interface PublicAbaRecordPageProps {
  abaRecordId?: string;
}

type LoadState = 'loading' | 'success' | 'error';

function formatMoney(value?: string | number | null, currency = 'USD') {
  const amount = typeof value === 'string' ? Number(value) : value;
  if (amount == null || Number.isNaN(amount)) return '-';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount);
}

function getQrImageSource(qrImage?: AbaRecord['qrImage']) {
  if (!qrImage) return null;

  if (Array.isArray(qrImage)) {
    const binary = String.fromCharCode(...qrImage);
    return `data:image/png;base64,${window.btoa(binary)}`;
  }

  if (qrImage.startsWith('data:') || qrImage.startsWith('http')) {
    return qrImage;
  }

  return `data:image/png;base64,${qrImage}`;
}

function getAbaDeepLink(record: AbaRecord | null) {
  if (!record) return null;

  const explicitDeepLink =
    record.abapay_deeplink || record.abapayDeeplink || record.deeplinkUrl || record.deepLink;

  if (explicitDeepLink) return explicitDeepLink;
  if (!record.qr) return null;

  return `abamobilebank://ababank.com?type=payway&qrcode=${encodeURIComponent(record.qr)}`;
}

function getStatusMeta(status?: AbaRecordStatus | null, isExpired = false) {
  if (status === 'CLOSED' || status === 'SUCCESS') {
    return {
      label: 'Paid',
      className: 'bg-emerald-50 text-emerald-700',
      icon: CheckCircle2,
    };
  }

  if (isExpired) {
    return {
      label: 'Expired',
      className: 'bg-slate-100 text-slate-600',
      icon: AlertCircle,
    };
  }

  if (status === 'PAYWAY') {
    return {
      label: 'Processing',
      className: 'bg-blue-50 text-blue-700',
      icon: Loader2,
    };
  }

  return {
    label: 'Awaiting payment',
    className: 'bg-amber-50 text-amber-700',
    icon: Clock,
  };
}

function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-sm mx-auto px-4">
          <PublicStoreHeader compact />
        </div>
      </div>
      <div className="max-w-sm mx-auto px-4 py-6">{children}</div>
    </div>
  );
}

export default function PublicAbaRecordPage({ abaRecordId }: PublicAbaRecordPageProps = {}) {
  const { id } = useParams();
  const effectiveAbaRecordId = abaRecordId ?? id;
  const [record, setRecord] = useState<AbaRecord | null>(null);
  const [status, setStatus] = useState<LoadState>(effectiveAbaRecordId ? 'loading' : 'error');
  const [error, setError] = useState<string | null>(null);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasOpenedDeepLink = useRef(false);

  const qrImageSource = useMemo(() => getQrImageSource(record?.qrImage), [record?.qrImage]);
  const deepLink = useMemo(() => getAbaDeepLink(record), [record]);
  const isExpired = useMemo(() => {
    if (!record?.expiredAt) return false;
    const expiredAt = new Date(record.expiredAt);
    return !Number.isNaN(expiredAt.getTime()) && expiredAt.getTime() <= Date.now();
  }, [record?.expiredAt]);

  const isPaid = useMemo(() => {
    return record?.status === 'SUCCESS' || record?.status === 'CLOSED';
  }, [record?.status]);

  const statusMeta = useMemo(
    () => getStatusMeta(record?.status, isExpired && !isPaid),
    [record?.status, isExpired, isPaid],
  );
  const StatusIcon = statusMeta.icon;

  const loadAbaRecord = useCallback(async (silent = false) => {
    if (!effectiveAbaRecordId) {
      if (!silent) {
        setStatus('error');
        setError('Missing ABA payment id.');
      }
      return;
    }

    if (!silent) {
      setStatus('loading');
      setError(null);
    }

    try {
      const response = await fetchAbaRecord(effectiveAbaRecordId);

      if (!response.success || !response.data) {
        if (!silent) {
          setStatus('error');
          setError(response.message || 'Unable to load ABA payment.');
        }
        return;
      }

      setRecord(response.data);
      if (!silent) {
        setStatus('success');
      }
    } catch (err: any) {
      if (!silent) {
        setStatus('error');
        setError(String(err?.message ?? err));
      }
    }
  }, [effectiveAbaRecordId]);

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setIsMobileDevice(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    }
  }, []);

  useEffect(() => {
    loadAbaRecord();
  }, [loadAbaRecord]);

  useEffect(() => {
    if (!record || isPaid || isExpired) return;

    const interval = setInterval(() => {
      loadAbaRecord(true);
    }, 5000);

    return () => clearInterval(interval);
  }, [record, isPaid, isExpired, loadAbaRecord]);

  useEffect(() => {
    if (!isMobileDevice) return;
    if (!deepLink || hasOpenedDeepLink.current || isExpired || isPaid) return;

    hasOpenedDeepLink.current = true;
    setIsRedirecting(true);
    const timer = window.setTimeout(() => {
      window.location.href = deepLink;
    }, 500);

    return () => window.clearTimeout(timer);
  }, [deepLink, isExpired, isPaid, isMobileDevice]);

  const handleOpenAba = useCallback(() => {
    if (!deepLink) return;
    window.location.href = deepLink;
  }, [deepLink]);

  if (status === 'loading') {
    return (
      <PageShell>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="mt-4 text-sm text-slate-600">Loading...</p>
        </div>
      </PageShell>
    );
  }

  if (status === 'error' || !record) {
    return (
      <PageShell>
        <div className="py-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Payment not found</AlertTitle>
            <AlertDescription className="mt-2 space-y-4">
              <p>{error || 'This payment link is invalid or expired.'}</p>
              <Button onClick={() => loadAbaRecord(false)} variant="outline" className="w-full">
                <RefreshCcw className="w-4 h-4 mr-2" />
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </PageShell>
    );
  }

  if (isPaid) {
    return (
      <PageShell>
        <div className="flex flex-col items-center text-center py-10 space-y-8">
          <div className="flex flex-col items-center space-y-5">
            <div className="relative flex items-center justify-center">
              <div className="absolute h-20 w-20 rounded-full bg-emerald-100/80" />
              <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 shadow-sm">
                <CheckCircle2 className="h-8 w-8 text-white" strokeWidth={2.5} />
              </div>
            </div>

            <div className="space-y-1">
              <h1 className="text-xl font-semibold text-slate-900">អ្នកបានទូទាត់រួចរាល់</h1>
              <p className="text-sm text-slate-500">Your transaction has been completed</p>
            </div>
          </div>

          <div className="w-full space-y-1">
            <p className="text-sm font-medium text-slate-500">Amount paid</p>
            <p className="text-4xl font-semibold tracking-tight text-slate-950">
              {formatMoney(record.amount, record.currency || 'USD')}
            </p>
            {record.merchantName && (
              <p className="pt-1 text-sm text-slate-500">{record.merchantName}</p>
            )}
          </div>

          <div className="w-full border-t border-slate-200 pt-6 space-y-1">
            <p className="text-sm font-medium text-slate-700">សូមអរគុណសម្រាប់ការបញ្ជាទិញរបស់អ្នក!</p>
            <p className="text-xs text-slate-400">Thank you for your payment</p>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="space-y-6 py-2">
        <div className="text-center space-y-2">
          <p className="text-3xl font-semibold tracking-tight text-slate-950">
            {formatMoney(record.amount, record.currency || 'USD')}
          </p>
          {record.merchantName && (
            <p className="text-sm text-slate-500">{record.merchantName}</p>
          )}
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium',
              statusMeta.className,
            )}
          >
            <StatusIcon
              className={cn('w-3.5 h-3.5', statusMeta.label === 'Processing' && 'animate-spin')}
            />
            {statusMeta.label}
          </span>
        </div>

        {isExpired ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Payment expired</AlertTitle>
            <AlertDescription>Please request a new payment link.</AlertDescription>
          </Alert>
        ) : (
          <>
            {isRedirecting && (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                <Smartphone className="w-4 h-4 text-blue-600" />
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span>Opening ABA Mobile...</span>
              </div>
            )}

            <div className="mx-auto w-full max-w-[260px]">
              {qrImageSource ? (
                <img src={qrImageSource} alt="Payment QR code" className="w-full h-auto" />
              ) : record.qr ? (
                <QRCode value={record.qr} size={260} className="h-auto w-full max-w-full" />
              ) : (
                <p className="text-sm text-slate-500 text-center">QR unavailable</p>
              )}
            </div>

            <Button onClick={handleOpenAba} disabled={!deepLink} className="w-full h-11">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open ABA App
            </Button>
          </>
        )}
      </div>
    </PageShell>
  );
}
